import API from '../config/api';
import React, { useEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import axios from 'axios';

/*
  Floating notifications button for mobile. Sits in the bottom-right stack
  near the ShopGenie / Help launchers. Tapping it opens a bottom-sheet panel.
  Hidden on desktop (the navbar bell handles it there) and for guests.
*/
function NotificationLauncher() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');

  useEffect(() => {
    if (!userInfo?.email) return;

    axios
      .get(`${API}/notifications/${userInfo.email}`)
      .then(({ data }) => setNotifications(data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.email]);

  /* The mobile speed-dial opens this sheet via an event */
  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener('open-notifications', openHandler);
    return () => window.removeEventListener('open-notifications', openHandler);
  }, []);

  const unread = notifications.filter((n) => !n.isRead).length;

  /* Keep other widgets (speed-dial badge) in sync with the unread count */
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('unread-count-changed', { detail: unread })
    );
  }, [unread]);

  /* Viewing counts as reading: when the sheet closes after being open,
     mark everything that's still unread as read. */
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      return;
    }

    if (!wasOpen.current) return;

    const unreadItems = notifications.filter((n) => !n.isRead);
    if (!unreadItems.length) return;

    unreadItems.forEach((n) => {
      axios
        .put(`${API}/notifications/read/${n._id}`)
        .catch(() => {});
    });

    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/read/${id}`);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleClick = async (n) => {
    await markRead(n._id);
    setOpen(false);
    if (n.ticketId) navigate(`/ticket/${n.ticketId}`);
  };

  if (!userInfo) return null;

  return (
    <>
      <button
        type="button"
        className={'notif-launcher' + (unread > 0 ? ' has-unread' : '')}
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
      >
        <span className="notif-launcher-name">Alerts</span>
        🔔
        {unread > 0 && <span className="notif-launcher-badge">{unread}</span>}
      </button>

      {open && (
        <>
          <div className="notif-sheet-backdrop" onClick={() => setOpen(false)} />
          <div className="notif-sheet" role="dialog" aria-label="Notifications">
            <div className="notif-sheet-header">
              <span>
                <span className="notif-bell">🔔</span> Notifications
              </span>
              {unread > 0 && (
                <span className="notif-sheet-new">{unread} new</span>
              )}
            </div>

            {notifications.length === 0 ? (
              <div className="notif-sheet-empty">
                <div className="notif-empty-icon">🔕</div>
                <div className="fw-semibold mt-1">No notifications</div>
                <small className="text-muted">You're all caught up!</small>
              </div>
            ) : (
              <div className="notif-sheet-list">
                {notifications.slice(0, 12).map((n) => (
                  <button
                    type="button"
                    key={n._id}
                    className={'notif-sheet-item' + (!n.isRead ? ' unread' : '')}
                    onClick={() => handleClick(n)}
                  >
                    <span className="notif-sheet-icon">
                      {n.isRead ? '📩' : '✉️'}
                    </span>
                    <span className="notif-sheet-body">
                      <span className="notif-sheet-title">
                        {n.title}
                        {!n.isRead && <span className="notif-sheet-dot" />}
                      </span>
                      <span className="notif-sheet-msg">{n.message}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <style>{notifLauncherStyles}</style>
    </>
  );
}

const notifLauncherStyles = `
  .notif-launcher { display: none; }

  @media (max-width: 1199.98px) {
    /* The floating bell is replaced by the speed-dial's "Alerts" action;
       only the bottom-sheet remains, opened via the 'open-notifications'
       event. */
    .notif-launcher { display: none !important; }
    .notif-launcher-hidden {
      display: flex;
      align-items: center;
      justify-content: center;
      position: fixed;
      right: 16px;
      bottom: calc(268px + env(safe-area-inset-bottom, 0px));
      width: 52px;
      height: 52px;
      border: none;
      border-radius: 50%;
      font-size: 26px;
      color: #fff;
      background: linear-gradient(135deg, #b45309, #f59e0b);
      box-shadow: 0 10px 26px rgba(180, 83, 9, 0.45);
      z-index: 1050;
      cursor: pointer;
      transition: transform 0.2s ease;
    }
    .notif-launcher:active { transform: scale(0.92); }
    .notif-launcher.has-unread { animation: notifRing 2.5s ease-in-out infinite; }
    @keyframes notifRing {
      0%, 88%, 100% { transform: rotate(0); }
      90% { transform: rotate(12deg); }
      93% { transform: rotate(-10deg); }
      96% { transform: rotate(6deg); }
    }
    .notif-launcher-name {
      position: absolute;
      top: calc(100% + 3px);
      left: 50%;
      transform: translateX(-50%);
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 0.3px;
      color: #b45309;
      background: #fff;
      padding: 3px 11px;
      border-radius: 12px;
      box-shadow: 0 4px 10px rgba(180, 83, 9, 0.28);
      white-space: nowrap;
      pointer-events: none;
    }
    .notif-launcher-badge {
      position: absolute;
      top: -3px;
      right: -3px;
      min-width: 20px;
      height: 20px;
      padding: 0 5px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #dc2626;
      color: #fff;
      font-size: 0.66rem;
      font-weight: 700;
      border-radius: 20px;
      box-shadow: 0 0 0 2px #fff;
    }

    /* Backdrop + bottom sheet */
    .notif-sheet-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.45);
      z-index: 1058;
      animation: notifFade 0.2s ease;
    }
    @keyframes notifFade { from { opacity: 0; } to { opacity: 1; } }

    .notif-sheet {
      position: fixed;
      left: 10px;
      right: 10px;
      bottom: calc(78px + env(safe-area-inset-bottom, 0px));
      z-index: 1059;
      background: #fff;
      border-radius: 18px;
      overflow: hidden;
      max-height: 66vh;
      display: flex;
      flex-direction: column;
      box-shadow: 0 -12px 44px rgba(59, 7, 100, 0.35);
      animation: notifUp 0.24s ease;
    }
    @keyframes notifUp {
      from { opacity: 0; transform: translateY(18px); }
      to { opacity: 1; transform: translateY(0); }
    }
    /* Sanctum header with a gold thread running beneath it */
    .notif-sheet-header {
      position: relative;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 16px;
      color: #fde68a;
      font-weight: 700;
      background: linear-gradient(120deg, #1a0611, #460d16 38%, #7c2d12 72%, #b45309);
    }
    .notif-sheet-header::after {
      content: '';
      position: absolute;
      left: 0; right: 0; bottom: 0;
      height: 2px;
      background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
      background-size: 200% 100%;
      animation: notifThread 4.5s linear infinite;
    }
    @keyframes notifThread {
      from { background-position: 200% 0; }
      to   { background-position: -200% 0; }
    }

    /* Temple bell — still, then a peal that damps out */
    .notif-bell {
      display: inline-block;
      transform-origin: 50% 10%;
      animation: notifTempleBell 6.5s ease-in-out infinite;
    }
    @keyframes notifTempleBell {
      0%, 60%, 100% { transform: rotate(0deg); }
      64% { transform: rotate(16deg); }
      68% { transform: rotate(-13deg); }
      72% { transform: rotate(9deg); }
      76% { transform: rotate(-6deg); }
      80% { transform: rotate(3deg); }
      85% { transform: rotate(-1deg); }
    }

    .notif-sheet-new {
      background: linear-gradient(135deg, #fde68a, #fbbf24);
      color: #3b1206;
      font-size: 0.72rem;
      font-weight: 700;
      border-radius: 30px;
      padding: 3px 10px;
      animation: notifNewPulse 2.2s ease-in-out infinite;
    }
    @keyframes notifNewPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.65); }
      50%      { box-shadow: 0 0 0 8px rgba(251, 191, 36, 0); }
    }
    .notif-sheet-list {
      overflow-y: auto;
      background: linear-gradient(160deg, #1a0611 0%, #460d16 55%, #7c2d12 100%);
      scrollbar-width: thin;
      scrollbar-color: rgba(253, 230, 138, 0.45) transparent;
    }
    .notif-sheet-list::-webkit-scrollbar { width: 8px; }
    .notif-sheet-list::-webkit-scrollbar-track { background: transparent; }
    .notif-sheet-list::-webkit-scrollbar-thumb {
      background: rgba(253, 230, 138, 0.4);
      border-radius: 8px;
    }

    .notif-sheet-item {
      position: relative;
      overflow: hidden;
      display: flex;
      gap: 12px;
      width: 100%;
      text-align: left;
      border: none;
      background: transparent;
      padding: 13px 16px;
      border-bottom: 1px solid rgba(253, 230, 138, 0.16);
      cursor: pointer;
      animation: notifItemIn 0.32s ease both;
    }
    @keyframes notifItemIn {
      from { opacity: 0; transform: translateX(-10px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    .notif-sheet-item:nth-child(2) { animation-delay: 0.04s; }
    .notif-sheet-item:nth-child(3) { animation-delay: 0.08s; }
    .notif-sheet-item:nth-child(4) { animation-delay: 0.12s; }
    .notif-sheet-item:nth-child(5) { animation-delay: 0.16s; }
    .notif-sheet-item:nth-child(6) { animation-delay: 0.20s; }
    /* Gold wick lighting up the row */
    .notif-sheet-item::before {
      content: '';
      position: absolute;
      left: 0; top: 50%;
      width: 3px; height: 0;
      border-radius: 3px;
      background: linear-gradient(180deg, #fde68a, #f59e0b);
      box-shadow: 0 0 10px rgba(251, 191, 36, 0.8);
      transform: translateY(-50%);
      transition: height 0.25s ease;
    }
    .notif-sheet-item:active::before { height: 60%; }
    .notif-sheet-item.unread { background: rgba(253, 230, 138, 0.08); }
    .notif-sheet-item:active { background: rgba(253, 230, 138, 0.16); }
    .notif-sheet-icon {
      flex-shrink: 0;
      width: 38px;
      height: 38px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: rgba(253, 230, 138, 0.16);
      border: 1px solid rgba(253, 230, 138, 0.35);
      font-size: 1rem;
    }
    .notif-sheet-body { min-width: 0; flex: 1; }
    .notif-sheet-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 700;
      font-size: 0.9rem;
      color: #fde68a;
    }
    /* Unread envelopes sit in a breathing lamp-glow */
    .notif-sheet-item.unread .notif-sheet-icon {
      animation: notifAura 3.2s ease-in-out infinite;
    }
    @keyframes notifAura {
      0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.3); }
      50%      { box-shadow: 0 0 16px 3px rgba(245, 158, 11, 0.6); }
    }

    /* The unread dot burns like a small diya */
    .notif-sheet-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #f59e0b;
      flex-shrink: 0;
      animation: notifLamp 1.7s ease-in-out infinite;
    }
    @keyframes notifLamp {
      0%, 100% { opacity: 0.65; transform: scale(1);    box-shadow: 0 0 0 0 rgba(245,158,11,0.7); }
      50%      { opacity: 1;    transform: scale(1.25); box-shadow: 0 0 9px 2px rgba(245,158,11,0.8); }
    }
    .notif-sheet-msg {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      font-size: 0.82rem;
      color: rgba(245, 230, 200, 0.72);
      margin-top: 2px;
    }
    .notif-sheet-empty {
      text-align: center;
      padding: 30px 16px;
      color: #fde68a;
      background: linear-gradient(160deg, #1a0611 0%, #460d16 55%, #7c2d12 100%);
    }
    .notif-sheet-empty .text-muted {
      color: rgba(245, 230, 200, 0.65) !important;
    }
    /* The silenced bell sways gently while there's nothing to report */
    .notif-empty-icon {
      font-size: 2rem;
      display: inline-block;
      transform-origin: 50% 12%;
      animation: notifIdleSway 4.5s ease-in-out infinite;
    }
    @keyframes notifIdleSway {
      0%, 100% { transform: rotate(-6deg); }
      50%      { transform: rotate(6deg); }
    }

    @media (prefers-reduced-motion: reduce) {
      .notif-sheet-header::after,
      .notif-bell,
      .notif-sheet-new,
      .notif-sheet-item.unread .notif-sheet-icon,
      .notif-sheet-dot,
      .notif-empty-icon,
      .notif-sheet-item,
      .notif-sheet,
      .notif-sheet-backdrop { animation: none; }
    }
  }
`;

export default NotificationLauncher;
